import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, AlertTriangle, DollarSign, Users, ArrowUpRight, ArrowDownRight, Activity, Wallet, ArrowLeftRight, Percent, Truck, UtensilsCrossed, ClipboardList, Calculator } from 'lucide-react';
export function DashboardView() {
  return <div className="space-y-6" data-id="element-112">
      <div className="flex items-center justify-between" data-id="element-113">
        <h2 className="text-2xl font-bold text-slate-900" data-id="element-114">
          Business Health Overview
        </h2>
        <div className="flex items-center gap-2 text-sm font-medium text-slate-500 bg-white px-3 py-1.5 rounded-lg border border-slate-100 shadow-sm" data-id="element-115">
          <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" data-id="element-116" />
          Live Data: Today, May 31, 2026
        </div>
      </div>

      {/* Primary KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-117">
        <KpiCard title="Today's Gross Sales" value="$12,450.00" change="+12.5%" isPositive={true} icon={DollarSign} color="emerald" data-id="element-118" />
        <KpiCard title="Labor %" value="18.4%" change="-2.1%" isPositive={true} icon={Users} color="blue" data-id="element-119" />
        <KpiCard title="Variance to Plan" value="+1.2%" change="vs. 22% Plan" isPositive={false} icon={Activity} color="purple" data-id="element-120" />
        <KpiCard title="MTD Food Cost %" value="21.8%" change="Target: 22%" isPositive={true} icon={Percent} color="indigo" data-id="element-121" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-122">
        {/* Food Cost Gauge & Alerts */}
        <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-123">
          <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm" data-id="element-124">
            <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-6" data-id="element-125">
              Current Food Cost %
            </h3>
            <div className="flex flex-col items-center justify-center py-4" data-id="element-126">
              <div className="relative w-48 h-24 overflow-hidden" data-id="element-127">
                <div className="absolute top-0 left-0 w-48 h-48 rounded-full border-[16px] border-slate-100" data-id="element-128" />
                <div className="absolute top-0 left-0 w-48 h-48 rounded-full border-[16px] border-emerald-500" style={{
                clipPath: 'polygon(0 0, 100% 0, 100% 50%, 0 50%)',
                transform: 'rotate(-45deg)'
              }} data-id="element-129" />
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 text-center" data-id="element-130">
                  <span className="text-3xl font-black text-slate-900" data-id="element-131">
                    21.8%
                  </span>
                  <p className="text-[10px] font-bold text-emerald-600 uppercase" data-id="element-132">
                    Healthy
                  </p>
                </div>
              </div>
              <div className="flex justify-between w-full mt-4 px-4 text-[10px] font-bold text-slate-400" data-id="element-133">
                <span data-id="element-134">0%</span>
                <span data-id="element-135">22% (PLAN)</span>
                <span data-id="element-136">40%</span>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm" data-id="element-137">
            <div className="flex items-center justify-between mb-6" data-id="element-138">
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider" data-id="element-139">
                Low Inventory Alerts
              </h3>
              <span className="px-2 py-1 bg-amber-100 text-amber-700 text-[10px] font-bold rounded-md" data-id="element-140">
                3 ITEMS
              </span>
            </div>
            <div className="space-y-4" data-id="element-141">
              <InventoryAlert item="Atlantic Salmon" stock="4.2 lbs" threshold="10 lbs" data-id="element-142" />
              <InventoryAlert item="Heavy Cream" stock="2 qts" threshold="5 qts" data-id="element-143" />
              <InventoryAlert item="Brioche Buns" stock="12 units" threshold="48 units" data-id="element-144" />
            </div>
          </div>
        </div>

        {/* Secondary Metrics */}
        <div className="bg-slate-900 rounded-2xl p-6 text-white shadow-xl" data-id="element-145">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-6" data-id="element-146">
            Financial Snapshot
          </h3>
          <div className="space-y-6" data-id="element-147">
            <MetricRow label="Adj. CC Deposit" value="$8,420.50" icon={DollarSign} data-id="element-148" />
            <MetricRow label="Weekly HSE/Chrg" value="$1,240.00" icon={TrendingUp} data-id="element-149" />
            <MetricRow label="Pending Xfers" value="+$450.00" icon={ArrowLeftRight} data-id="element-150" />
            <MetricRow label="Petty Cash Balance" value="$350.00" icon={Wallet} data-id="element-151" />
            <div className="pt-4 border-t border-white/10" data-id="element-152">
              <p className="text-[10px] font-bold text-slate-500 uppercase mb-2" data-id="element-153">
                Top Spending Vendor
              </p>
              <div className="flex items-center justify-between" data-id="element-154">
                <span className="font-bold" data-id="element-155">Sysco Foods</span>
                <span className="text-emerald-400 font-bold" data-id="element-156">$4,280.12</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Management Statistics Section */}
      <div data-id="element-157">
        <h3 className="text-lg font-bold text-slate-900 mb-4" data-id="element-158">
          Management Overview
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4" data-id="element-159">
          <StatCard title="P&L Recap" value="+1.4%" label="Net Profit Variance" icon={Calculator} color="indigo" data-id="element-160" />
          <StatCard title="Payroll" value="$24,850" label="Total MTD" icon={Users} color="blue" data-id="element-161" />
          <StatCard title="Vendors" value="12" label="Active Partners" icon={Truck} color="emerald" data-id="element-162" />
          <StatCard title="Recipes" value="84" label="Avg. 21.5% Cost" icon={UtensilsCrossed} color="orange" data-id="element-163" />
          <StatCard title="Prep List" value="18/24" label="75% Complete" icon={ClipboardList} color="purple" data-id="element-164" />
        </div>
      </div>
    </div>;
}
function StatCard({
  title,
  value,
  label,
  icon: Icon,
  color
}: any) {
  const colors: any = {
    indigo: 'bg-indigo-50 text-indigo-600',
    blue: 'bg-blue-50 text-blue-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    orange: 'bg-orange-50 text-orange-600',
    purple: 'bg-purple-50 text-purple-600'
  };
  return <motion.div whileHover={{
    y: -2
  }} className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm" data-id="element-165">
      <div className="flex items-center gap-3 mb-3" data-id="element-166">
        <div className={`p-2 rounded-lg ${colors[color]}`} data-id="element-167">
          <Icon size={16} data-id="element-168" />
        </div>
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-169">
          {title}
        </p>
      </div>
      <p className="text-xl font-black text-slate-900 mb-0.5" data-id="element-170">{value}</p>
      <p className="text-[10px] font-medium text-slate-500" data-id="element-171">{label}</p>
    </motion.div>;
}
function KpiCard({
  title,
  value,
  change,
  isPositive,
  icon: Icon,
  color
}: any) {
  const colors: any = {
    emerald: 'bg-emerald-50 text-emerald-600',
    blue: 'bg-blue-50 text-blue-600',
    purple: 'bg-purple-50 text-purple-600',
    indigo: 'bg-indigo-50 text-indigo-600'
  };
  return <motion.div whileHover={{
    y: -2
  }} className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm" data-id="element-172">
      <div className="flex items-start justify-between mb-4" data-id="element-173">
        <div className={`p-2.5 rounded-xl ${colors[color]}`} data-id="element-174">
          <Icon size={20} data-id="element-175" />
        </div>
        <div className={`flex items-center gap-1 text-[10px] font-bold ${isPositive ? 'text-emerald-600' : 'text-amber-600'}`} data-id="element-176">
          {isPositive ? <ArrowUpRight size={12} data-id="element-177" /> : <ArrowDownRight size={12} data-id="element-178" />}
          {change}
        </div>
      </div>
      <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1" data-id="element-179">
        {title}
      </p>
      <p className="text-2xl font-black text-slate-900" data-id="element-180">{value}</p>
    </motion.div>;
}
function InventoryAlert({
  item,
  stock,
  threshold
}: any) {
  return <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100" data-id="element-181">
      <div className="flex items-center gap-3" data-id="element-182">
        <div className="w-2 h-2 bg-amber-500 rounded-full" data-id="element-183" />
        <span className="text-xs font-bold text-slate-700" data-id="element-184">{item}</span>
      </div>
      <div className="text-right" data-id="element-185">
        <p className="text-[10px] font-bold text-slate-900" data-id="element-186">{stock}</p>
        <p className="text-[8px] font-bold text-slate-400 uppercase" data-id="element-187">
          Min: {threshold}
        </p>
      </div>
    </div>;
}
function MetricRow({
  label,
  value,
  icon: Icon
}: any) {
  return <div className="flex items-center justify-between" data-id="element-188">
      <div className="flex items-center gap-3" data-id="element-189">
        <div className="w-8 h-8 bg-white/5 rounded-lg flex items-center justify-center text-slate-400" data-id="element-190">
          <Icon size={16} data-id="element-191" />
        </div>
        <span className="text-xs font-bold text-slate-300" data-id="element-192">{label}</span>
      </div>
      <span className="text-sm font-black text-white" data-id="element-193">{value}</span>
    </div>;
}